import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { ArrowBack, Save, CloudUpload } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';

const API_BASE_URL = import.meta.env.DEV
  ? `http://localhost:3000/api/v1`
  : 'https://back.famalink.app/api/v1';

function resolveImageUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return API_BASE_URL.replace('/api/v1', '') + url;
}

const ArticleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  // Form state
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [externalUrl, setExternalUrl] = useState('');
  const [status, setStatus] = useState('draft');
  const [imageAlt, setImageAlt] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');

  // Options
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<any[]>([]);

  // UI state
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    apiService.getArticleTags().then((res: any) => setAvailableTags(res.data.tags || [])).catch(() => {});
    apiService.getArticleCategories().then((res: any) => setAvailableCategories(res.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    apiService.getArticle(Number(id))
      .then((res: any) => {
        const a = res.data.article || res.data;
        setTitle(a.title || '');
        setSummary(a.summary || '');
        setBody(a.body || '');
        setCategory(a.category || 'best-practices');
        setTags(a.tags || []);
        setExternalUrl(a.external_url || '');
        setStatus(a.status || 'draft');
        setImageAlt(a.image_alt || '');
        if (a.image_url) setImagePreview(resolveImageUrl(a.image_url));
      })
      .catch(() => setError('Failed to load article'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const toggleTag = (slug: string) => {
    setTags((prev) => prev.includes(slug) ? prev.filter((t) => t !== slug) : [...prev, slug]);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('summary', summary);
      fd.append('body', body);
      fd.append('category', category);
      fd.append('tags', tags.join(','));
      fd.append('external_url', externalUrl);
      fd.append('status', status);
      fd.append('image_alt', imageAlt);
      if (imageFile) fd.append('image', imageFile);

      if (isEdit) {
        await apiService.updateArticle(Number(id), fd);
        setSuccess('Article updated');
      } else {
        await apiService.createArticle(fd);
        setSuccess('Article created');
        setTimeout(() => navigate('/articles'), 1000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/articles')}>
            Back to Articles
          </Button>
          <Typography variant="h4">{isEdit ? 'Edit Article' : 'New Article'}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  fullWidth
                  error={!title.trim()}
                  helperText={!title.trim() ? 'Title is required' : ''}
                />
              </Grid>

              {/* Category + Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                    {availableCategories.map((c) => (
                      <MenuItem key={c.id} value={c.slug}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags <Typography component="span" variant="caption" color="text.secondary">(select one or more)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableTags.map((t) => (
                    <Chip
                      key={t.id}
                      label={t.name}
                      onClick={() => toggleTag(t.slug)}
                      color={tags.includes(t.slug) ? 'primary' : 'default'}
                      variant={tags.includes(t.slug) ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>

              {/* Summary */}
              <Grid item xs={12}>
                <TextField
                  label="Summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="Short summary shown on the article card"
                />
              </Grid>

              {/* Body */}
              <Grid item xs={12}>
                <TextField
                  label="Body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  multiline
                  rows={6}
                  fullWidth
                  placeholder="Article content (for original articles without an external URL)"
                />
              </Grid>

              {/* Cover Image */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Cover Image</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {imagePreview && (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="cover preview"
                      sx={{ width: 160, height: 100, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                    />
                  )}
                  <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                    {imageFile ? 'Change Image' : 'Upload Image'}
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                  </Button>
                  {imageFile && <Typography variant="caption" color="text.secondary">{imageFile.name}</Typography>}
                </Box>
              </Grid>

              {/* Image Alt */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Image Alt Text"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  fullWidth
                  placeholder="Descriptive text for accessibility"
                />
              </Grid>

              {/* External URL */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="External URL"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  fullWidth
                  placeholder="Link to original article (optional)"
                />
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ArticleForm;
